import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, X, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function EarlyAccessDialog({ open, onClose, interestedPlan }) {
  const [form, setForm] = useState({ email: "", name: "", organization: "" });
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  if (!open) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    if (!accepted) {
      toast.error("Please accept the Terms and Privacy Policy to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/early-access", { ...form, interested_plan: interestedPlan || null });
      setDone({ status: r.data.status });
      if (r.data.status === "joined") toast.success("You are on the early-access list!");
      else toast.info("You are already on the list.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="early-access-dialog" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl fade-in-up">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C07A60]">Early Access</p>
            <h3 className="mt-1 font-heading font-semibold text-xl text-[#1C211F]">
              Get notified when Nexari Health launches
            </h3>
            <p className="mt-1 text-sm text-[#57605A]">
              We are finalizing our Terms & Privacy Policy. Subscriptions open shortly.
              {interestedPlan && <> Interested in <strong className="text-[#1C211F]">{interestedPlan}</strong>.</>}
            </p>
          </div>
          <button data-testid="early-access-close-btn" onClick={onClose} className="text-[#57605A] hover:text-[#1C211F]" aria-label="Close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {done ? (
          <div className="mt-6 text-center py-4">
            <div className="w-12 h-12 bg-[#E6F0EB] rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-7 h-7 text-[#2E5C50]" strokeWidth={1.5} />
            </div>
            <h4 className="mt-3 font-heading font-semibold text-lg text-[#1C211F]">
              {done.status === "joined" ? "You are on the list" : "Already registered"}
            </h4>
            <p className="mt-2 text-sm text-[#57605A]">
              We will email you the moment Nexari Health is open for sign-ups. No spam.
            </p>
            <button data-testid="early-access-done-btn" onClick={onClose} className="mt-5 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2 text-sm font-medium">
              Got it
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-3">
            <input data-testid="ea-email-input" required type="email" placeholder="Your work email" value={form.email} onChange={set("email")}
              className="w-full bg-white border border-[#E8E6E1] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]" />
            <input data-testid="ea-name-input" type="text" placeholder="Your name (optional)" value={form.name} onChange={set("name")}
              className="w-full bg-white border border-[#E8E6E1] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]" />
            <input data-testid="ea-org-input" type="text" placeholder="Agency / organization (optional)" value={form.organization} onChange={set("organization")}
              className="w-full bg-white border border-[#E8E6E1] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]" />
            <label className="flex items-start gap-2 pt-1 text-xs text-[#57605A] cursor-pointer select-none">
              <input
                data-testid="ea-terms-checkbox"
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-[#E8E6E1] text-[#2E5C50] focus:ring-[#2E5C50]"
              />
              <span>
                I agree to be contacted about Nexari and accept the{" "}
                <Link to="/terms" target="_blank" rel="noopener" className="text-[#2E5C50] underline" data-testid="ea-terms-link">Terms of Service</Link>
                {" "}and{" "}
                <Link to="/privacy" target="_blank" rel="noopener" className="text-[#2E5C50] underline" data-testid="ea-privacy-link">Privacy Policy</Link>.
              </span>
            </label>
            <button data-testid="ea-submit-btn" type="submit" disabled={submitting || !accepted}
              className="w-full flex items-center justify-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md py-2.5 text-sm font-medium disabled:opacity-60">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Notify me at launch
            </button>
            <p className="text-xs text-[#57605A] text-center pt-1 inline-flex items-center gap-1 w-full justify-center">
              <ShieldCheck className="w-3 h-3" strokeWidth={1.5} /> One email at launch — unsubscribe anytime.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
